/**
* @license
* 
* (C) Copyright IBM Corp. 2017, 2025. All Rights Reserved.
* 
* Licensed under the Apache License, Version 2.0 (the "License"); you may not use this file except
* in compliance with the License. You may obtain a copy of the License at
* 
* http://www.apache.org/licenses/LICENSE-2.0
* 
* Unless required by applicable law or agreed to in writing, software distributed under the License
* is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express
* or implied. See the License for the specific language governing permissions and limitations under
* the License.
* 
* @carbon/ai-chat 0.3.2
* 
* Built: Jun 11 2025 5:48 pm +00:00
* 
* 
*/

import { _ as __decorate, c as carbonElement } from './customElement.js';
import './cds-aichat-container.js';
import { LitElement, html } from 'lit';
import { property, state } from 'lit/decorators.js';
import 'react';
import './AppContainer.js';
import 'react-intl';
import '@carbon/icons-react';
import '@carbon/react';
import 'react-redux';
import '@carbon/web-components/es-custom/components/button/index.js';
import '@carbon/web-components/es-custom/components/overflow-menu/index.js';
import 'lit/directives/unsafe-html.js';
import '@carbon/web-components/es-custom/components/data-table/index.js';
import '@carbon/web-components/es-custom/components/checkbox/index.js';
import '@carbon/icon-helpers';
import '@carbon/icons';
import 'lit/directives/unsafe-svg.js';
import 'lit-html/directives/repeat.js';
import '@carbon/web-components/es-custom/components/pagination/index.js';
import '@carbon/web-components/es-custom/components/select/index.js';
import '@carbon/web-components/es-custom/components/data-table/table-skeleton.js';
import '@carbon/web-components/es-custom/components/slug/index.js';
import '@carbon/web-components/es-custom/components/ai-label/defs.js';
import '@carbon/web-components/es-custom/components/popover/defs.js';
import '@carbon/web-components/es-custom/components/skeleton-icon/index.js';
import 'react-dom';
import '@carbon/web-components/es-custom/components/ai-label/ai-label-action-button.js';
import '@carbon/web-components/es-custom/components/ai-label/ai-label.js';
import '@carbon/web-components/es-custom/components/inline-loading/index.js';
import '@carbon/web-components/es-custom/components/textarea/index.js';
import '@carbon/web-components/es-custom/components/icon-button/index.js';
import '@carbon/web-components/es-custom/components/tag/index.js';
import '@carbon/web-components/es-custom/components/chat-button/index.js';
import '@carbon/web-components/es-custom/components/button/button.js';
import '@carbon/web-components/es-custom/components/layer/index.js';
import 'react-dom/client';

/**
 * cds-aichat-custom-element will is a pass through to cds-aichat-container. It takes any user_defined and writeable element
 * slotted content and forwards it to cds-aichat-container. It also will setup the custom element with a default viewChange
 * pattern (e.g. hiding and showing the custom element when the chat should be open/closed) if a onViewChange property is not
 * defined. Finally, it registers the custom element with cds-aichat-container so a default "floating" element will not be created.
 */
let ChatCustomElement = class ChatCustomElement extends LitElement {
    constructor() {
        super(...arguments);
        this._originalStyles = { width: this.style.width, height: this.style.height };
        /**
         * A default handler for the "view:change" event. This will be used to show or hide the web chat main window
         * using a simple classname.
         */
        this.defaultViewChangeHandler = (event, instance) => {
            if (event.newViewState.mainWindow) {
                this._styles = { width: this._originalStyles?.width, height: this._originalStyles?.height };
                instance.elements.getMainWindow().removeClassName('HideWebChat');
            }
            else {
                this._originalStyles = { width: this.style.width, height: this.style.height };
                this._styles = { width: '0px', height: '0px' };
                instance.elements.getMainWindow().addClassName('HideWebChat');
            }
        };
        /**
         * The existing array of slot names for all user_defined components.
         */
        this._userDefinedSlotNames = [];
        /**
         * The existing array of slot names for all writeable elements.
         */
        this._writeableElementSlots = [];
        /**
         * Adds the slot attribute to the element for the user_defined response type and then injects it into the component by
         * updating this._userDefinedSlotNames;
         */
        this.userDefinedHandler = (event) => {
            // This element already has `slot` as an attribute.
            const { slot } = event.data;
            if (!this._userDefinedSlotNames.includes(slot)) {
                this._userDefinedSlotNames = [...this._userDefinedSlotNames, slot];
            }
        };
        this.onBeforeRenderOverride = async (instance) => {
            this._instance = instance;
            this._instance.on({ type: "view:change" /* BusEventType.VIEW_CHANGE */, handler: this.onViewChange || this.defaultViewChangeHandler });
            this._instance.on({ type: "userDefinedResponse" /* BusEventType.USER_DEFINED_RESPONSE */, handler: this.userDefinedHandler });
            this._instance.on({ type: "chunk:userDefinedResponse" /* BusEventType.CHUNK_USER_DEFINED_RESPONSE */, handler: this.userDefinedHandler });
            this.addWriteableElementSlots();
            await this.onBeforeRender?.(instance);
        };
    }
    addWriteableElementSlots() {
        const writeableElementSlots = [this._instance.customPanels.getPanel().hostElement.getAttribute('slot')];
        Object.keys(this._instance.writeableElements).forEach(writeableElementKey => {
            writeableElementSlots.push(writeableElementKey);
        });
        this._writeableElementSlots = writeableElementSlots;
    }
    firstUpdated() {
        this._configOverride = {
            ...this.config,
            element: this,
        };
    }
    updated(changedProperties) {
        if (changedProperties.has('config')) {
            this._configOverride = {
                ...this.config,
                element: this,
            };
            this.requestUpdate();
        }
    }
    renderStyles() {
        if (this._styles?.width && this._styles?.height) {
            return html `<style>
        :host {
          width: ${this._styles.width};
          height: ${this._styles.height};
        }
      </style>`;
        }
        return '';
    }
    /**
     * Renders the template while passing in class functionality
     */
    render() {
        return html `${this.renderStyles()}
      <cds-aichat-container
        .config=${this._configOverride}
        .onAfterRender=${this.onAfterRender}
        .onBeforeRender=${this.onBeforeRenderOverride}
      >
        ${this._writeableElementSlots.map(slot => html `<div slot=${slot}><slot name=${slot}></slot></div>`)}
        ${this._userDefinedSlotNames.map(slot => html `<div slot=${slot}><slot name=${slot}></slot></div>`)}
      </cds-aichat-container>`;
    }
};
__decorate([
    property({ type: Object })
], ChatCustomElement.prototype, "config", void 0);
__decorate([
    property()
], ChatCustomElement.prototype, "onBeforeRender", void 0);
__decorate([
    property()
], ChatCustomElement.prototype, "onAfterRender", void 0);
__decorate([
    property()
], ChatCustomElement.prototype, "onViewChange", void 0);
__decorate([
    state()
], ChatCustomElement.prototype, "_configOverride", void 0);
__decorate([
    state()
], ChatCustomElement.prototype, "_originalStyles", void 0);
__decorate([
    state()
], ChatCustomElement.prototype, "_styles", void 0);
__decorate([
    state()
], ChatCustomElement.prototype, "_userDefinedSlotNames", void 0);
__decorate([
    state()
], ChatCustomElement.prototype, "_writeableElementSlots", void 0);
__decorate([
    state()
], ChatCustomElement.prototype, "_instance", void 0);
ChatCustomElement = __decorate([
    carbonElement('cds-aichat-custom-element')
], ChatCustomElement);
var ChatCustomElement$1 = ChatCustomElement;

export { ChatCustomElement$1 as default };
